import React from 'react';
import ReactDOM from 'react-dom';
import theme from '../../theme';
import {omit, map, compact, get} from 'lodash';
import List, {ListProps} from '@material-ui/core/List';
import {autobind} from 'core-decorators';
import memoize from 'lodash-decorators/memoize';

interface DraggableListProps<T> extends ListProps {
    placeholderStyle: React.CSSProperties;
    onDraggableListChange: (values: T[]) => void;
    isTopDroppable: boolean;
    dragStyle: React.CSSProperties;
}

interface DraggableListState {
    dropIndex: number | null;
    dragIndex: number | null;
    isDragStyleFrozen: boolean;
}

export default class DraggableList<T>
    extends React.Component<DraggableListProps<T>, DraggableListState> {

    childRefs: React.RefObject<React.ReactInstance>[] = [];
    childMaxDropY: (null | number)[] = [];
    lastDragY = -Infinity;

    constructor(props: DraggableListProps<T>) {
        super(props);

        this.state = {
            dropIndex: null,
            dragIndex: null,
            isDragStyleFrozen: false
        };
    }

    componentDidMount() {
        document.addEventListener('dragover', this.onDocumentDragOver);
    }

    componentWillUnmount() {
        document.removeEventListener('dragover', this.onDocumentDragOver);
    }

    componentDidUpdate(prevProps: DraggableListProps<T>) {
        if (prevProps.children !== this.props.children && this.state.dragIndex !== null) {
            this.cacheChildMaxDropY();
        }
    }

    render() {
        const children = compact(React.Children.toArray(this.props.children)).map(this.renderChild);

        const {dropIndex} = this.state;
        if (dropIndex !== null) children.splice(dropIndex, 0, this.renderDropPlaceholder());

        return <List
            {...omit(
                this.props,
                'isTopDroppable',
                'onDraggableListChange',
                'children',
                'dragStyle',
                'placeholderStyle'
            )}
        >{children}</List>;
    }

    @autobind
    renderChild(child: React.ReactNode, index: number) {
        if (!React.isValidElement(child)) return;
        if (!this.childRefs[index]) this.childRefs[index] = React.createRef();
        const ref = this.childRefs[index];

        let style;
        if (this.state.dragIndex !== index) {
            style = {};
        } else if (!this.state.isDragStyleFrozen) {
            style = {cursor: '-webkit-grabbing', ...this.props.dragStyle};
        } else {
            style = {cursor: '-webkit-grabbing', ...this.props.placeholderStyle};
        }

        return React.cloneElement(child, {
            ref,
            onDragStart: this.onChildDragStart(index),
            onDragEnd: this.onChildDragEnd,
            style: {...(child.props as any).style, ...style}
        } as any);
    }

    renderDropPlaceholder() {
        return (<div
            key="dropPlaceholder"
            style={{
                height: theme.spacing(0.5),
                backgroundColor: theme.palette.primary.main,
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1)
            }}
        />);
    }

    @memoize
    @autobind
    onChildDragStart(index: number) {
        return (event: React.DragEvent) => {
            this.cacheChildMaxDropY();

            this.setState({dragIndex: index});

            // Styles applied while "isDragStyleFrozen" is false will be applied
            // to the version of the element that is following the cursor.
            // Styles applied while "isDragStyleFrozen" is true will be applied to
            // stationary version.
            setTimeout(() => this.setState({isDragStyleFrozen: true}));

            // Needed to enable dragging effect in Firefox
            event.dataTransfer.setData('Text', ' ');
            event.dataTransfer.effectAllowed = 'move';
        };
    }

    @autobind
    onChildDragEnd() {
        if (this.state.dropIndex !== null && this.state.dragIndex !== null) {
            this.props.onDraggableListChange(
                move(
                    map(this.childRefs, ref =>
                        get(ref, 'current.props["data-draggable"]') ||
                        get(ref, 'current.dataset.draggable')
                    ),
                    this.state.dragIndex,
                    this.state.dropIndex
                )
            );
        }

        this.setState({
            dragIndex: null,
            dropIndex: null,
            isDragStyleFrozen: false
        });
    }

    // Firefox only provides mouse coordinates to "dragover" events, not
    // "drag" events.
    @autobind
    onDocumentDragOver(event: DragEvent) {
        const {dragIndex} = this.state;

        // dragIndex will be null if the dragged element does not belong to
        // this list.
        if (dragIndex === null) return;

        const dragY: number = event.clientY;
        const dragX: number = event.clientX;
        let dropIndex: number | null;

        // Ignore garbage events at (0, 0)
        // Ignore events with no change in dragY to reduce CPU load.
        if ((dragX === 0 && dragY === 0) || dragY - this.lastDragY === 0) {
            dropIndex = this.state.dropIndex;

        } else {
            this.lastDragY = dragY;

            for (dropIndex = 0; dropIndex < this.childMaxDropY.length; dropIndex++) {
                const maxDropY = this.childMaxDropY[dropIndex];
                if (maxDropY !== null && dragY <= maxDropY) break;
            }

            // Dragging an item before or after itself is a no-op.
            if (dropIndex === dragIndex || dropIndex === dragIndex + 1) {
                dropIndex = null;
            }

            this.setState({dropIndex});
        }

        if (dropIndex !== null && event && event.dataTransfer) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        }
    }

    cacheChildMaxDropY() {
        this.childMaxDropY = [];
        for (let index = 0; index < this.childRefs.length; index++) {
            const childRef = this.childRefs[index];
            if (!this.props.isTopDroppable && index === 0) {
                this.childMaxDropY[index] = null;
            } else if (childRef.current === null) {
                this.childMaxDropY[index] = null;
            } else {
                // eslint-disable-next-line react/no-find-dom-node
                const node = ReactDOM.findDOMNode(childRef.current);
                this.childMaxDropY[index] = node instanceof HTMLElement ?
                    node.getBoundingClientRect().top + node.offsetHeight / 2 :
                    null;
            }
        }
    }

    static defaultProps = {isTopDroppable: true};
}

function move<T>(input: T[], from: number, to: number): T[] {
    if (to > from) to--;
    const output = input.slice(0);
    output.splice(to, 0, output.splice(from, 1)[0]);
    return output;
}
